import {useMutation, useQueryClient} from "@tanstack/react-query";
import {ChangeEvent, useEffect, useState} from "react";
import {useReadQnaDetail} from "./useReadQnaDetail";
import {useNavigate, useParams} from "react-router-dom";
import {
    DeleteQnaApiProps,
    QnaEntry,
    UpdateQnaStateApiProps,
    UpdateTechnicalSupportApiProps
} from "../../../../types/support/qna";
import {
    createQnaApi,
    deleteQnaApi,
    updateQnaApi,
    updateQnaStateApi,
    updateTechnicalSupportApi
} from "../../../../api/support/qna";
import {useModal} from "../../../../hooks";
import {useUser} from "../../../../util/userContext/UserContext";
import useFileHandler from "../../../../hooks/useFileHandler";

export const DefaultInputData = {
    title: "",
    newProductGroupid: '',
    topicTypeIdx: 0,
    productVendorIdx: 0,
    productCategoryIdx: 0,
    isNotice: "Y",
    body: "",
    qnaId: '',
    userId: 0,
    userName: '',
    technicalSupportUserName: '',
    companyName: '',
    questionState: '접수',
    topicType: '',
    productVendor: '',
    productCategory: '',
    technicalSupportId: 0,
    regAt: '',
    loginId: '',
    phoneNo: '',
    modAt: '',
    deleteFlag: 'N',
    replyList: [],
    fileKeyList: [], // 저장/수정할 때 사용
    fileList: [], // 저장/수정할 때 사용
    fileVOList: [], // 조회결과로 사용
};

/**
 * QNA CRUD hook
 * @returns
 */
export const useEditQna = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const {user} = useUser();
    const {handleModal, resetModal} = useModal();
    const {id} = useParams();
    const {data} = useReadQnaDetail({id: id || ""});
    const [inputData, setInputData] = useState<any>({
        ...DefaultInputData,
        userId: user?.userId,
        userName: user?.userName,
        loginId: user?.loginId
    });
    const {removeFile, UploadUI, handleButtonClick} = useFileHandler({setter: setInputData});

    const deleteMutation = useMutation({
        mutationFn: (data: DeleteQnaApiProps) => deleteQnaApi(data),
        onSuccess: (res) => {
            if (res.code === "0000") {
                handleModal({
                    isOpen: true,
                    contents: "삭제되었습니다.",
                    type: "alert",
                    closeAction: () => queryClient.invalidateQueries({
                        queryKey: ["readQnaApi"],
                    })
                });
            }
        },
        onError: (error) => {
            console.log("update error", error);
        },
    });

    const updateStateMutation = useMutation({
        mutationFn: (data: UpdateQnaStateApiProps) => updateQnaStateApi(data),
        onSuccess: (res) => {
            handleModal({
                isOpen: true,
                type: 'alert',
                contents: '수정되었습니다.',
                closeAction: () => queryClient.invalidateQueries({
                    queryKey: ["readQnaDetailApi"],
                })
            });
        },
        onError: (error) => {
            console.log("update error", error);
        },
    });

    const updateTechnicalSupportMutation = useMutation({
        mutationFn: (data: UpdateTechnicalSupportApiProps) => updateTechnicalSupportApi(data),
        onSuccess: (res) => {
            handleModal({
                isOpen: true,
                type: 'alert',
                contents: '수정되었습니다.',
                closeAction: () => queryClient.invalidateQueries({
                    queryKey: ["readQnaDetailApi"],
                })
            });
        },
        onError: (error) => {
            console.log("update error", error);
        },
    });

    const updateMutation = useMutation({
        mutationFn: (data: QnaEntry) => updateQnaApi(data),
        onSuccess: (res, variables) => {
            handleModal({
                isOpen: true,
                type: 'alert',
                contents: '수정되었습니다.',
                closeAction: () => {
                    queryClient.invalidateQueries({queryKey: ["readQnaDetailApi", variables.qnaId]});
                    queryClient.invalidateQueries({queryKey: ["readQnaApi"]});
                    navigate(`${process.env.PUBLIC_URL}/support/qna/detail/${variables.qnaId}`, {replace: true});
                }
            });
        },
        onError: (error) => {
            console.log("update error", error);
        },
    });

    const createMutation = useMutation({
        mutationFn: (data: QnaEntry) => createQnaApi(data),
        onSuccess: (res) => {
            handleModal({
                isOpen: true,
                type: 'alert',
                contents: '저장되었습니다.',
                closeAction: () => {
                    queryClient.invalidateQueries({queryKey: ["readQnaApi"]});
                    navigate(`${process.env.PUBLIC_URL}/support/qna/detail/${res.result.qnaId}`, {replace: true});
                }
            });
        },
        onError: (error) => {
            console.log("update error", error);
        },
    });

    const deleteQna = (data: DeleteQnaApiProps) => {
        if ((data?.qnaIdList?.length | 0) <= 0) {
            handleModal({
                isOpen: true,
                contents: "선택된 항목이 없습니다.",
                type: "alert",
            });
        } else {
            handleModal({
                isOpen: true,
                type: 'confirm',
                contents: "삭제하시겠습니까?",
                callback: () => {
                    deleteMutation.mutate(data);
                }
            });
        }
    };

    const deleteQnaAndBackToSearch = (data: DeleteQnaApiProps) => {
        if ((data?.qnaIdList?.length | 0) > 0) {
            handleModal({
                isOpen: true,
                type: 'confirm',
                contents: "삭제하시겠습니까?",
                callback: async () => {
                    deleteMutation.mutateAsync(data).then(res => {
                        if (res.code === "0000") {
                            handleModal({
                                isOpen: true,
                                contents: "삭제되었습니다.",
                                type: "alert",
                                closeAction: () => navigate(`${process.env.PUBLIC_URL}/support/qna/list`)
                            });
                        }
                    });
                }
            });
        }
    }

    const saveQna = (data: QnaEntry) => {
        if (inputData?.newProductGroupid === "") {
            handleModal({
                isOpen: true,
                contents: "제품군을 선택해주세요.",
                type: "alert",
            });
        } else {
            if (!data.qnaId) createMutation.mutate({...data, qnaId: id || ""});
            else updateMutation.mutate({...data, qnaId: id || ""});
        }
    };

    const updateQnaState = (data: UpdateQnaStateApiProps) => {
        handleModal({
            isOpen: true,
            type: 'confirm',
            contents: "수정하시겠습니까?",
            callback: () => updateStateMutation.mutate(data)
        });
    };

    const updateTechnicalSupport = (data: UpdateTechnicalSupportApiProps) => {
        handleModal({
            isOpen: true,
            type: 'confirm',
            contents: "수정하시겠습니까?",
            callback: () => updateTechnicalSupportMutation.mutate(data)
        });
    }

    const onChangeInputData = (
        e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const {name, value} = e.target;
        setInputData({...inputData, [name]: value});
    };

    useEffect(() => {
        if (id)
            setInputData(data);
    }, [data]);

    return {
        deleteQna,
        inputData,
        onChangeInputData,
        setInputData,
        updateQnaState, updateTechnicalSupport,
        deleteQnaAndBackToSearch,
        saveQna,
        removeFile, UploadUI, handleButtonClick
    };
};
